import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BookingWidget = ({ service, onBookingSubmit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timeSlots = [
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '13:00', label: '1:00 PM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' }
  ];

  const addons = [
    { id: 'deep-clean', name: 'Deep Clean Add-on', price: 50, description: 'Extra thorough cleaning' },
    { id: 'eco-products', name: 'Eco-Friendly Products', price: 25, description: 'Green cleaning solutions' },
    { id: 'carpet-protection', name: 'Carpet Protection', price: 75, description: 'Stain protection treatment' },
    { id: 'same-day', name: 'Same Day Service', price: 40, description: 'Rush service fee' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev =>
      prev?.includes(addonId)
        ? prev?.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotal = () => {
    const basePrice = service?.startingPrice;
    const addonPrice = selectedAddons?.reduce((total, addonId) => {
      const addon = addons?.find(a => a?.id === addonId);
      return total + (addon ? addon?.price : 0);
    }, 0);
    return basePrice + addonPrice;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = {
        service: service?.title,
        date: selectedDate,
        time: selectedTime,
        addons: selectedAddons,
        customer: formData,
        total: calculateTotal()
      };

      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Booking submitted:', bookingData);
      
      if (onBookingSubmit) {
        onBookingSubmit(bookingData);
      }
      
      alert('Booking request submitted successfully! We\'ll contact you shortly to confirm.');
      setIsOpen(false);
    } catch (error) {
      console.error('Booking error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Sticky Booking Button (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 z-40 lg:hidden">
        <Button
          variant="default"
          fullWidth
          onClick={() => setIsOpen(true)}
          iconName="Calendar"
          iconPosition="left"
        >
          Book This Service - ${service?.startingPrice}
        </Button>
      </div>
      {/* Booking Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-text-primary">
                  Book {service?.title}
                </h2>
                <p className="text-sm text-text-secondary">
                  Fill out the details below to schedule your service
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                aria-label="Close booking form"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Date & Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Preferred Date"
                    type="date"
                    required
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e?.target?.value)}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                  <Select
                    label="Preferred Time"
                    options={timeSlots}
                    value={selectedTime}
                    onChange={setSelectedTime}
                    placeholder="Select time slot"
                    required
                  />
                </div>

                {/* Customer Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-text-primary">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      type="text"
                      required
                      value={formData?.name}
                      onChange={(e) => handleInputChange('name', e?.target?.value)}
                      placeholder="Enter your full name"
                    />
                    <Input
                      label="Phone Number"
                      type="tel"
                      required
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <Input
                    label="Email Address"
                    type="email"
                    required
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    placeholder="Enter your email address"
                  />
                  <Input
                    label="Service Address"
                    type="text"
                    required
                    value={formData?.address}
                    onChange={(e) => handleInputChange('address', e?.target?.value)}
                    placeholder="Enter the address where service is needed"
                  />
                </div>

                {/* Add-ons */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-text-primary">
                    Optional Add-ons
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {addons?.map((addon) => (
                      <div
                        key={addon?.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                          selectedAddons?.includes(addon?.id)
                            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                        }`}
                        onClick={() => toggleAddon(addon?.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-text-primary">
                              {addon?.name}
                            </h4>
                            <p className="text-sm text-text-secondary mt-1">
                              {addon?.description}
                            </p>
                          </div>
                          <div className="text-right ml-3">
                            <div className="font-semibold text-text-primary">
                              +${addon?.price}
                            </div>
                            <div className={`w-5 h-5 rounded border-2 mt-1 ${
                              selectedAddons?.includes(addon?.id)
                                ? 'bg-primary border-primary' :'border-border'
                            }`}>
                              {selectedAddons?.includes(addon?.id) && (
                                <Icon name="Check" size={12} color="white" className="m-0.5" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Special Requests (Optional)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    placeholder="Any special instructions or requests..."
                    value={formData?.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e?.target?.value)}
                  />
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-medium text-text-primary">
                  Total Estimate:
                </span>
                <span className="text-2xl font-bold text-primary">
                  ${calculateTotal()}
                </span>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  iconName="Calendar"
                  iconPosition="left"
                  className="flex-1"
                >
                  Submit Booking Request
                </Button>
              </div>
              
              <p className="text-xs text-text-secondary text-center mt-3">
                This is a booking request. Final pricing will be confirmed after our team reviews your requirements.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingWidget;